import React from 'react';
import { motion } from 'framer-motion';
import { User, Package, Heart, MapPin, CreditCard, HelpCircle, Settings, Bell, ChevronRight, ShoppingBag, LogOut, Store, ShieldCheck, Lock, Truck, CheckCircle2, RefreshCcw } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useApp } from '../context/AppContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function CustomerDashboardScreen() {
  const {
    navigate,
    resetTo
  } = useNavigation();
  const {
    user,
    setUser,
    orders,
    wishlist,
    toggleVendorMode
  } = useApp();
  const handleLogout = () => {
    setUser(null);
    resetTo(Screen.WELCOME);
  };
  const handleSellOnJummall = () => {
    toggleVendorMode();
    navigate(Screen.VENDOR_SIGNUP);
  };
  // Order stats
  const processingCount = orders.filter(o => o.status === 'Processing').length;
  const shippedCount = orders.filter(o => o.status === 'Shipped').length;
  const deliveredCount = orders.filter(o => o.status === 'Delivered').length;
  const returnsCount = 0; // Mock
  const recentOrders = [...orders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  const quickActions = [{
    icon: Package,
    label: 'My Orders',
    screen: Screen.ORDERS,
    color: 'text-blue-500',
    bg: 'bg-blue-100 dark:bg-blue-900/30'
  }, {
    icon: Heart,
    label: 'Wishlist',
    screen: Screen.WISHLIST,
    color: 'text-red-500',
    bg: 'bg-red-100 dark:bg-red-900/30',
    badge: wishlist.length
  }, {
    icon: MapPin,
    label: 'Address Book',
    screen: Screen.ADDRESS_BOOK,
    color: 'text-green-500',
    bg: 'bg-green-100 dark:bg-green-900/30'
  }, {
    icon: CreditCard,
    label: 'Payment',
    screen: Screen.PAYMENT_METHODS,
    color: 'text-purple-500',
    bg: 'bg-purple-100 dark:bg-purple-900/30'
  }, {
    icon: HelpCircle,
    label: 'Help Center',
    screen: Screen.HELP_CENTER,
    color: 'text-orange-500',
    bg: 'bg-orange-100 dark:bg-orange-900/30'
  }, {
    icon: Settings,
    label: 'Settings',
    screen: Screen.SETTINGS,
    color: 'text-gray-500',
    bg: 'bg-gray-100 dark:bg-gray-800'
  }];
  const accountServices = [{
    icon: User,
    label: 'Edit Profile',
    screen: Screen.EDIT_PROFILE
  }, {
    icon: Lock,
    label: 'Change Password',
    screen: Screen.CHANGE_PASSWORD
  }, {
    icon: ShieldCheck,
    label: 'Two-Factor Auth',
    screen: Screen.TWO_FACTOR
  }, {
    icon: Bell,
    label: 'Notifications',
    screen: Screen.NOTIFICATIONS
  }];
  if (!user) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-6" data-id="element-392">
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6" data-id="element-393">
          <User className="w-10 h-10 text-primary" data-id="element-394" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-395">
          Welcome to JUMMALL
        </h2>
        <p className="text-center text-gray-500 dark:text-gray-400 mb-8" data-id="element-396">
          Login to view your dashboard, track orders, and manage your account.
        </p>
        <Button onClick={() => navigate(Screen.LOGIN)} className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-xl font-semibold mb-4" data-id="element-397">
          Login to Account
        </Button>
        <Button variant="outline" onClick={() => navigate(Screen.SIGNUP)} className="w-full border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 py-4 rounded-xl font-semibold" data-id="element-398">
          Create an Account
        </Button>
      </div>;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24" data-id="element-399">
      <Header title="My Account" showBack={false} data-id="element-400" />

      <div className="p-4 space-y-4" data-id="element-401">
        {/* User Greeting Header */}
        <div className="bg-gradient-to-br from-primary to-primary-600 p-5 rounded-2xl text-white shadow-lg flex items-center justify-between" data-id="element-402">
          <div className="flex items-center gap-4" data-id="element-403">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center text-xl font-bold border-2 border-white/30" data-id="element-404">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div data-id="element-405">
              <h2 className="text-xl font-bold" data-id="element-406">
                Hello, {user.name.split(' ')[0]}!
              </h2>
              <p className="text-sm text-white/80 mt-0.5" data-id="element-407">Member since 2024</p>
            </div>
          </div>
          <button onClick={() => navigate(Screen.NOTIFICATIONS)} className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors relative" data-id="element-408">
            <Bell className="w-5 h-5" data-id="element-409" />
            <span className="absolute top-2 right-2.5 w-2 h-2 bg-accent rounded-full border border-primary" data-id="element-410"></span>
          </button>
        </div>

        {/* Order Status Summary */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-50 dark:border-gray-700" data-id="element-411">
          <div className="flex justify-between items-center mb-4" data-id="element-412">
            <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-413">
              My Orders
            </h3>
            <button onClick={() => navigate(Screen.ORDERS)} className="text-sm font-medium text-primary flex items-center" data-id="element-414">
              View All <ChevronRight className="w-4 h-4" data-id="element-415" />
            </button>
          </div>
          <div className="grid grid-cols-4 gap-2" data-id="element-416">
            <button onClick={() => navigate(Screen.ORDERS)} className="flex flex-col items-center gap-2" data-id="element-417">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center relative" data-id="element-418">
                <Package className="w-5 h-5 text-blue-500" data-id="element-419" />
                {processingCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800" data-id="element-420">
                    {processingCount}
                  </span>}
              </div>
              <span className="text-[10px] font-medium text-gray-600 dark:text-gray-400 text-center" data-id="element-421">
                Processing
              </span>
            </button>
            <button onClick={() => navigate(Screen.ORDERS)} className="flex flex-col items-center gap-2" data-id="element-422">
              <div className="w-12 h-12 bg-orange-50 dark:bg-orange-900/20 rounded-full flex items-center justify-center relative" data-id="element-423">
                <Truck className="w-5 h-5 text-orange-500" data-id="element-424" />
                {shippedCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800" data-id="element-425">
                    {shippedCount}
                  </span>}
              </div>
              <span className="text-[10px] font-medium text-gray-600 dark:text-gray-400 text-center" data-id="element-426">
                Shipped
              </span>
            </button>
            <button onClick={() => navigate(Screen.ORDERS)} className="flex flex-col items-center gap-2" data-id="element-427">
              <div className="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center relative" data-id="element-428">
                <CheckCircle2 className="w-5 h-5 text-green-500" data-id="element-429" />
                {deliveredCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800" data-id="element-430">
                    {deliveredCount}
                  </span>}
              </div>
              <span className="text-[10px] font-medium text-gray-600 dark:text-gray-400 text-center" data-id="element-431">
                Delivered
              </span>
            </button>
            <button onClick={() => navigate(Screen.RETURN_REQUEST)} className="flex flex-col items-center gap-2" data-id="element-432">
              <div className="w-12 h-12 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center relative" data-id="element-433">
                <RefreshCcw className="w-5 h-5 text-red-500" data-id="element-434" />
                {returnsCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800" data-id="element-435">
                    {returnsCount}
                  </span>}
              </div>
              <span className="text-[10px] font-medium text-gray-600 dark:text-gray-400 text-center" data-id="element-436">
                Returns
              </span>
            </button>
          </div>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-3 gap-3" data-id="element-437">
          {quickActions.map((action, idx) => <motion.button key={action.label} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: idx * 0.05
        }} onClick={() => navigate(action.screen)} className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-sm border border-gray-50 dark:border-gray-700 flex flex-col items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 transition-all" data-id="element-438">
              <div className={`w-10 h-10 rounded-full ${action.bg} flex items-center justify-center relative`} data-id="element-439">
                <action.icon className={`w-5 h-5 ${action.color}`} data-id="element-440" />
                {action.badge !== undefined && action.badge > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-[9px] font-bold rounded-full flex items-center justify-center border border-white dark:border-gray-800" data-id="element-441">
                    {action.badge}
                  </span>}
              </div>
              <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 text-center leading-tight" data-id="element-442">
                {action.label}
              </span>
            </motion.button>)}
        </div>

        {/* Recent Orders */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-50 dark:border-gray-700" data-id="element-443">
          <h3 className="font-bold text-gray-900 dark:text-white mb-3" data-id="element-444">
            Recent Purchases
          </h3>
          {recentOrders.length === 0 ? <div className="text-center py-6" data-id="element-445">
              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-446">
                <ShoppingBag className="w-6 h-6 text-gray-400" data-id="element-447" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-448">
                You haven't placed any orders yet.
              </p>
              <Button size="sm" onClick={() => navigate(Screen.HOME)} className="bg-primary hover:bg-primary/90 text-white" data-id="element-449">
                Start Shopping
              </Button>
            </div> : <div className="space-y-3" data-id="element-450">
              {recentOrders.map(order => <div key={order.id} onClick={() => navigate(Screen.ORDER_DETAIL)} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-id="element-451">
                  <img src={order.items[0].product.image} alt={order.items[0].product.name} className="w-12 h-12 rounded-lg object-cover bg-white dark:bg-gray-800" data-id="element-452" />
                  <div className="flex-1 min-w-0" data-id="element-453">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-454">
                      {order.items[0].product.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-455">
                      {new Date(order.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-456">
                    <p className="text-sm font-bold text-primary" data-id="element-457">
                      ₦{order.total.toLocaleString()}
                    </p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium inline-block mt-1 ${order.status === 'Delivered' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : order.status === 'Shipped' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'}`} data-id="element-458">
                      {order.status}
                    </span>
                  </div>
                </div>)}
            </div>}
        </div>

        {/* Account Services */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-50 dark:border-gray-700 overflow-hidden" data-id="element-459">
          <div className="p-4 border-b border-gray-50 dark:border-gray-700" data-id="element-460">
            <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-461">
              Account Settings
            </h3>
          </div>
          {accountServices.map((service, idx) => <button key={service.label} onClick={() => navigate(service.screen)} className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${idx !== accountServices.length - 1 ? 'border-b border-gray-50 dark:border-gray-700' : ''}`} data-id="element-462">
              <div className="flex items-center gap-3" data-id="element-463">
                <div className="w-8 h-8 rounded-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center" data-id="element-464">
                  <service.icon className="w-4 h-4 text-gray-600 dark:text-gray-400" data-id="element-465" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-466">
                  {service.label}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" data-id="element-467" />
            </button>)}
        </div>

        {/* Sell on JUMMALL Banner */}
        <motion.button whileTap={{
        scale: 0.98
      }} onClick={handleSellOnJummall} className="w-full bg-gradient-to-r from-accent to-orange-500 p-4 rounded-2xl text-white shadow-md flex items-center justify-between" data-id="element-468">
          <div className="flex items-center gap-3" data-id="element-469">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center" data-id="element-470">
              <Store className="w-5 h-5" data-id="element-471" />
            </div>
            <div className="text-left" data-id="element-472">
              <h3 className="font-bold" data-id="element-473">Start Selling on JUMMALL</h3>
              <p className="text-xs text-white/80 mt-0.5" data-id="element-474">
                Reach millions of customers today
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5" data-id="element-475" />
        </motion.button>

        {/* Logout */}
        <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 p-4 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-50 dark:border-gray-700 text-red-500 font-bold hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors" data-id="element-476">
          <LogOut className="w-5 h-5" data-id="element-477" />
          Log Out
        </button>
      </div>
    </motion.div>;
}